/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/ChangePassword.tsx
 * PURPOSE: Self-service password change page for tenant admin first login
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-01-27
 * COMPLIANCE: SDLC 6.3.1 + Sprint 40 Gap 1 (P0 - 2 SP)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Authentication, Vietnamese F&B Tenant Management
 * - Impact: Production Blocker (Phase 2.1 Test Dependency)
 * - Stakeholders: Tenant Admins, Platform Admins, NQH Pilot Users
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React 18 Hooks, CSS Modules, Design Tokens
 * - Dependencies: React Router, Auth Store, API Client
 *
 * @cross-references
 * - Test Plan: docs/05-test/02-Test-Plans/COMPREHENSIVE-TEST-PLAN-APPROVED-JAN27-2026.md
 * - Gap 1: Line 854-867 (Self-Service Password Change)
 * - Phase 2.1: Tenant Admin First Login Test
 * - Backend API: POST /api/v1/auth/change-password
 *
 * @sprint-40-gap-1
 * - CTO Constraints: Vietnamese keyboard support (no special chars)
 * - Password Policy: Min 8 chars, 1 number, 1 upper, 1 lower
 * - Session Invalidation: token_version increment on backend
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import { getDashboardRouteForRole } from "../utils/roleDashboardMap";
import styles from "./ChangePassword.module.css";

// i18n storage key
const LANGUAGE_KEY = "nqh-language";

// Change Password page translations
const translations = {
  vi: {
    title: "Đổi mật khẩu",
    subtitle: "Bạn phải đổi mật khẩu trước khi sử dụng hệ thống",
    currentPasswordLabel: "Mật khẩu hiện tại",
    newPasswordLabel: "Mật khẩu mới",
    confirmPasswordLabel: "Xác nhận mật khẩu mới",
    submit: "Đổi mật khẩu",
    submitting: "Đang đổi mật khẩu...",
    success: "Đổi mật khẩu thành công! Đang chuyển hướng...",
    passwordRequirements: {
      title: "Yêu cầu mật khẩu:",
      minLength: "Tối thiểu 8 ký tự",
      number: "Ít nhất 1 số",
      uppercase: "Ít nhất 1 chữ in hoa",
      lowercase: "Ít nhất 1 chữ thường",
      noSpecialRequired: "(Không bắt buộc ký tự đặc biệt - hỗ trợ bàn phím tiếng Việt)",
    },
    error: {
      empty: "Vui lòng nhập đầy đủ thông tin",
      mismatch: "Mật khẩu mới không khớp",
      same: "Mật khẩu mới phải khác mật khẩu hiện tại",
      minLength: "Mật khẩu phải có ít nhất 8 ký tự",
      noNumber: "Mật khẩu phải chứa ít nhất 1 số",
      noUppercase: "Mật khẩu phải chứa ít nhất 1 chữ in hoa",
      noLowercase: "Mật khẩu phải chứa ít nhất 1 chữ thường",
      incorrect: "Mật khẩu hiện tại không đúng",
      network: "Lỗi kết nối. Vui lòng thử lại.",
    },
  },
  en: {
    title: "Change Password",
    subtitle: "You must change your password before using the system",
    currentPasswordLabel: "Current Password",
    newPasswordLabel: "New Password",
    confirmPasswordLabel: "Confirm New Password",
    submit: "Change Password",
    submitting: "Changing password...",
    success: "Password changed successfully! Redirecting...",
    passwordRequirements: {
      title: "Password requirements:",
      minLength: "Minimum 8 characters",
      number: "At least 1 number",
      uppercase: "At least 1 uppercase letter",
      lowercase: "At least 1 lowercase letter",
      noSpecialRequired: "(No special characters required - Vietnamese keyboard support)",
    },
    error: {
      empty: "Please fill in all fields",
      mismatch: "New passwords do not match",
      same: "New password must be different from current password",
      minLength: "Password must be at least 8 characters",
      noNumber: "Password must contain at least 1 number",
      noUppercase: "Password must contain at least 1 uppercase letter",
      noLowercase: "Password must contain at least 1 lowercase letter",
      incorrect: "Current password is incorrect",
      network: "Connection error. Please try again.",
    },
  },
};

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { changePassword, user } = useAuthStore();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Language preference from localStorage
  const [language, setLanguage] = useState<"vi" | "en">(
    (localStorage.getItem(LANGUAGE_KEY) as "vi" | "en") || "vi"
  );
  const t = translations[language];

  // Password strength validation (Vietnamese keyboard-friendly)
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return t.error.minLength;
    }
    if (!/\d/.test(password)) {
      return t.error.noNumber;
    }
    if (!/[A-Z]/.test(password)) {
      return t.error.noUppercase;
    }
    if (!/[a-z]/.test(password)) {
      return t.error.noLowercase;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t.error.empty);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t.error.mismatch);
      return;
    }

    if (newPassword === currentPassword) {
      setError(t.error.same);
      return;
    }

    // Vietnamese keyboard-friendly password validation
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);

      // BOT-005 fix: this page was originally TA-first-login only (hardcoded
      // /tenant-dashboard); now also reached by any role forced here via
      // must_change_password (Login.tsx), so redirect by the user's actual role.
      const dashboard = getDashboardRouteForRole(user?.role);
      setTimeout(() => {
        navigate(dashboard);
      }, 2000);
    } catch (err: any) {
      console.error("Password change error:", err);

      // Handle specific error messages
      if (err.response?.data?.detail?.includes("incorrect")) {
        setError(t.error.incorrect);
      } else if (err.message?.includes("network") || err.message?.includes("fetch")) {
        setError(t.error.network);
      } else {
        setError(err.response?.data?.detail || err.message || t.error.network);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Real-time password strength indicator
  const getPasswordStrength = (password: string): { label: string; color: string; progress: number } => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/\d/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;

    if (strength <= 25) return { label: language === "vi" ? "Yếu" : "Weak", color: "#DC2626", progress: strength };
    if (strength <= 50) return { label: language === "vi" ? "Trung bình" : "Fair", color: "#F59E0B", progress: strength };
    if (strength <= 75) return { label: language === "vi" ? "Tốt" : "Good", color: "#10B981", progress: strength };
    return { label: language === "vi" ? "Mạnh" : "Strong", color: "#059669", progress: strength };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          {/* 2026-09-04 (BUG-03, @tester phát hiện lúc verify luồng nhân viên):
              màn này trước chỉ là CỔNG BẮT BUỘC lúc đăng nhập lần đầu nên không
              cần lối thoát. Nay nó còn là 1 tile tự nguyện ở Tiện ích
              (Utilities.tsx:205) ⇒ ai bấm vào là mắc kẹt, không có đường ra
              trong app. Thêm nút quay lại — nhưng CHỈ khi vào tự nguyện:
              `must_change_password` = true nghĩa là đang bị ÉP đổi, hiện nút
              thoát lúc đó sẽ cho phép lách yêu cầu bắt buộc. */}
          {!user?.must_change_password && (
            <button
              type="button"
              onClick={() => navigate("/utilities")}
              className={styles.backLink}
              aria-label={language === "vi" ? "Quay lại" : "Back"}
              data-testid="change-password-back"
            >
              ‹ {language === "vi" ? "Quay lại" : "Back"}
            </button>
          )}
          <h1 className={styles.title}>{t.title}</h1>
          <p className={styles.subtitle}>{t.subtitle}</p>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          {/* Current Password */}
          <div className={styles.inputGroup}>
            <label htmlFor="currentPassword" className={styles.label}>
              {t.currentPasswordLabel}
            </label>
            <div className={styles.passwordInput}>
              <input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={styles.input}
                disabled={isLoading || success}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className={styles.togglePassword}
                disabled={isLoading || success}
              >
                {showCurrentPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className={styles.inputGroup}>
            <label htmlFor="newPassword" className={styles.label}>
              {t.newPasswordLabel}
            </label>
            <div className={styles.passwordInput}>
              <input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={styles.input}
                disabled={isLoading || success}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className={styles.togglePassword}
                disabled={isLoading || success}
              >
                {showNewPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            {/* Password Strength Meter */}
            {newPassword && (
              <div className={styles.strengthMeter}>
                <div
                  className={styles.strengthBar}
                  style={{
                    width: `${passwordStrength.progress}%`,
                    backgroundColor: passwordStrength.color,
                  }}
                />
                <span className={styles.strengthLabel} style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className={styles.inputGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              {t.confirmPasswordLabel}
            </label>
            <div className={styles.passwordInput}>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={styles.input}
                disabled={isLoading || success}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={styles.togglePassword}
                disabled={isLoading || success}
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className={styles.requirements}>
            <p className={styles.requirementsTitle}>{t.passwordRequirements.title}</p>
            <ul className={styles.requirementsList}>
              <li className={newPassword.length >= 8 ? styles.met : ""}>
                {t.passwordRequirements.minLength}
              </li>
              <li className={/\d/.test(newPassword) ? styles.met : ""}>
                {t.passwordRequirements.number}
              </li>
              <li className={/[A-Z]/.test(newPassword) ? styles.met : ""}>
                {t.passwordRequirements.uppercase}
              </li>
              <li className={/[a-z]/.test(newPassword) ? styles.met : ""}>
                {t.passwordRequirements.lowercase}
              </li>
            </ul>
            <p className={styles.noSpecialNote}>{t.passwordRequirements.noSpecialRequired}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className={styles.success} role="alert">
              {t.success}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading || success}
          >
            {isLoading ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
